import React, { createContext, ReactNode, useEffect, useState, useCallback } from "react";
import FirebaseService from "services/FirebaseService";
import { MessagePayload } from "firebase/messaging";
import { EVENTS, NOTIFICATION_TYPES, ONLINE_ORDER_SOURCES } from "const";
import orderReceivedAudio from "../assets/audio/order_received.mp3";
import shortBeepToneAudio from "../assets/audio/short_beep_tone.mp3";
import NotificationHandler from "utils/NotificationHandler";
import { eventsEmitter } from "utils/events";

export type OnlineOrderNotification = {
  id: number;
  source?: string;
  title?: string;
  message?: string;
  receivedAt: string;
};

export type NotificationContextType = {
  reCreateAndStoreNotificationToken: () => Promise<void>;
  activeAlertOrderId: number | null;
  activeAlertOrderSource: string | null;
  isAlerting: boolean;
  isRinging: boolean;
  onlineOrderNotifications: OnlineOrderNotification[];
  startRinging: () => void;
  stopRinging: () => void;
  dismissAlert: () => void;
  triggerNewOrderAlert: (orderId: number, source?: string, title?: string, message?: string) => void;
  openOnlineOrderDetails: (orderId: number) => void;
  closeOnlineOrderDetails: () => void;
  selectedOrderIdForModal: number | null;
};

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

type NotificationProviderProps = {
  children: ReactNode;
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [activeAlertOrderId, setActiveAlertOrderId] = useState<number | null>(null);
  const [activeAlertOrderSource, setActiveAlertOrderSource] = useState<string | null>(null);
  const [isAlerting, setIsAlerting] = useState<boolean>(false);
  const [isRinging, setIsRinging] = useState<boolean>(false);
  const [onlineOrderNotifications, setOnlineOrderNotifications] = useState<OnlineOrderNotification[]>([]);
  const [selectedOrderIdForModal, setSelectedOrderIdForModal] = useState<number | null>(null);

  const [voiceAudio, setVoiceAudio] = useState<HTMLAudioElement | null>(null);
  const [beepAudio, setBeepAudio] = useState<HTMLAudioElement | null>(null);

  // Initialize audio elements using imported audio assets

  useEffect(() => {
    let voice: HTMLAudioElement | null = null;
    let beep: HTMLAudioElement | null = null;

    try {
      voice = new Audio(orderReceivedAudio);
      beep = new Audio(shortBeepToneAudio);
      beep.loop = true;

      setVoiceAudio(voice);
      setBeepAudio(beep);
    } catch (e) {
      console.error("Failed to initialize notification audio:", e);
    }

    return () => {
      if (voice) {
        voice.pause();
      }
      if (beep) {
        beep.pause();
      }
    };
  }, []);

  const stopRinging = useCallback(() => {
    try {
      if (voiceAudio) {
        voiceAudio.pause();
        voiceAudio.currentTime = 0;
      }
      if (beepAudio) {
        beepAudio.pause();
        beepAudio.currentTime = 0;
      }
    } catch (err) {
      console.warn("Error stopping audio:", err);
    }
    setIsRinging(false);
  }, [voiceAudio, beepAudio]);

  const startRinging = useCallback(() => {
    stopRinging();
    setIsRinging(true);

    try {
      if (voiceAudio) {
        voiceAudio.currentTime = 0;
        voiceAudio.play().catch((err) => console.warn("Voice audio play blocked by browser policy:", err));
      }

      if (beepAudio) {
        beepAudio.currentTime = 0;
        beepAudio.play().catch((err) => console.warn("Beep audio play blocked by browser policy:", err));
      }
    } catch (err) {
      console.warn("Error starting sound alert:", err);
    }
  }, [voiceAudio, beepAudio, stopRinging]);

  const dismissAlert = useCallback(() => {
    stopRinging();
    setIsAlerting(false);
    setActiveAlertOrderId(null);
    setActiveAlertOrderSource(null);
  }, [stopRinging]);

  const openOnlineOrderDetails = useCallback(
    (orderId: number) => {
      stopRinging();
      setIsAlerting(false);
      setSelectedOrderIdForModal(orderId);
    },
    [stopRinging],
  );

  const closeOnlineOrderDetails = useCallback(() => {
    stopRinging();
    setSelectedOrderIdForModal(null);
  }, [stopRinging]);

  const triggerNewOrderAlert = useCallback(
    (orderId: number, source?: string, title?: string, message?: string) => {
      const orderSource = source || ONLINE_ORDER_SOURCES.SANOFT_CART;
      console.log(`[NotificationContext] New Online Order received: #${orderId} from ${orderSource}`);

      setActiveAlertOrderId(orderId);
      setActiveAlertOrderSource(orderSource);
      setIsAlerting(true);

      const newNotification: OnlineOrderNotification = {
        id: orderId,
        source: orderSource,
        title: title || "New Online Order",
        message: message || `Received new online order #${orderId}`,
        receivedAt: new Date().toISOString(),
      };

      setOnlineOrderNotifications((prev) => [newNotification, ...prev.filter((n) => n.id !== orderId)]);

      startRinging();
    },
    [startRinging],
  );

  const processNewNotification = useCallback(
    (payload: MessagePayload) => {
      console.log("FCM Payload received in context:", payload);
      const type = payload.data?.type || NOTIFICATION_TYPES.NEW_ONLINE_ORDER;
      const title = payload.data?.title || "Notification";
      const body = payload.data?.message || "";

      // Check if this is an online order notification
      if (type === NOTIFICATION_TYPES.NEW_ONLINE_ORDER && payload.data.id) {
        const orderId = Number(payload.data.id);
        if (!isNaN(orderId)) {
          triggerNewOrderAlert(orderId, payload.data.source, title, body);
          return;
        }
      }
    },
    [triggerNewOrderAlert],
  );

  useEffect(() => {
    const unsubscribe = FirebaseService.onForegroundMessage(processNewNotification);

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [processNewNotification]);

  useEffect(() => {
    const unsubscribeFn = eventsEmitter.on(EVENTS.NEW_NOTIFICATION, processNewNotification);
    return () => {
      if (unsubscribeFn) {
        unsubscribeFn();
      }
    };
  }, [processNewNotification]);

  const reCreateAndStoreNotificationToken = useCallback(async (): Promise<void> => {
    // FCM token recreation logic
    console.log("Re creating FCM token");

    try {
      await NotificationHandler.reCreateAndStoreNotificationToken();
    } catch (error) {
      console.log("Recreating FCM token failed: ", error);
    }
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        reCreateAndStoreNotificationToken,
        activeAlertOrderId,
        activeAlertOrderSource,
        isAlerting,
        isRinging,
        onlineOrderNotifications,
        startRinging,
        stopRinging,
        dismissAlert,
        triggerNewOrderAlert,
        openOnlineOrderDetails,
        closeOnlineOrderDetails,
        selectedOrderIdForModal,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
